import { lineAmount, type MfgPurchaseOrderLine } from "@/lib/manufacturing/purchaseOrderTypes";
import type { InventoryPoNeed } from "@/lib/manufacturing/poDraftFromInventory";

export type PoDraftLine = {
  key: string;
  material_id: string | null;
  unit_id: string | null;
  material_name: string;
  description: string;
  quantity: string;
  rate: string;
  expected_pieces: string;
};

export function newDraftLine(): PoDraftLine {
  return {
    key: crypto.randomUUID(),
    material_id: null,
    unit_id: null,
    material_name: "",
    description: "",
    quantity: "1",
    rate: "",
    expected_pieces: "",
  };
}

export function draftLineAmount(line: PoDraftLine): number | null {
  const qty = Number(line.quantity);
  const rate = line.rate.trim() === "" ? null : Number(line.rate);
  if (!Number.isFinite(qty)) return null;
  return lineAmount(qty, rate);
}

export function serializeDraftLines(lines: PoDraftLine[]) {
  return lines.map((l, i) => {
    const qty = Number(l.quantity);
    const rate = l.rate.trim() === "" ? null : Number(l.rate);
    const expected =
      l.expected_pieces.trim() === "" ? null : Number(l.expected_pieces);
    return {
      material_id: l.material_id,
      unit_id: l.unit_id,
      material_name: l.material_name.trim(),
      description: l.description.trim(),
      quantity: qty,
      rate,
      expected_pieces: expected,
      sort_order: i,
    };
  });
}

export function linesFromOrder(lines: MfgPurchaseOrderLine[] | undefined): PoDraftLine[] {
  if (!lines?.length) return [newDraftLine()];
  return lines.map((l) => ({
    key: l.id || crypto.randomUUID(),
    material_id: l.material_id,
    unit_id: l.unit_id ?? null,
    material_name: l.material_name,
    description: l.description,
    quantity: String(l.quantity),
    rate: l.rate == null ? "" : String(l.rate),
    expected_pieces: l.expected_pieces == null ? "" : String(l.expected_pieces),
  }));
}

export function appendInventoryNeeds(
  lines: PoDraftLine[],
  needs: InventoryPoNeed[]
): { lines: PoDraftLine[]; added: number } {
  const have = new Set(lines.map((l) => l.material_id).filter((id): id is string => Boolean(id)));
  const add = needs
    .filter((n) => !have.has(n.material_id))
    .map((n) => ({
      ...newDraftLine(),
      material_id: n.material_id,
      material_name: n.material_name,
      quantity: String(n.quantity),
      rate: n.rate == null ? "" : String(n.rate),
    }));
  if (add.length === 0) return { lines, added: 0 };
  const kept = lines.filter((l) => l.material_name.trim());
  return { lines: kept.length ? [...kept, ...add] : add, added: add.length };
}
